import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { AlertTriangle, Package, Layers, CheckCircle2, XCircle } from 'lucide-react';
import apiService from '../services/api';

/**
 * Modal de confirmation pour la mise en rupture d'un ingrédient
 * Affiche la liste des produits impactés avec cases à cocher pour sélection
 */
const ImpactProduitsModal = ({ 
  isOpen, 
  onClose, 
  ingredient, 
  commercantId,
  onConfirm 
}) => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProduits, setSelectedProduits] = useState([]);
  const [selectedElements, setSelectedElements] = useState([]);

  useEffect(() => {
    if (isOpen && ingredient && commercantId) {
      loadImpact();
    } else {
      // Reset quand le modal se ferme
      setImpact(null);
      setSelectedProduits([]);
      setSelectedElements([]);
      setError('');
    }
  }, [isOpen, ingredient, commercantId]);

  const loadImpact = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.request(
        `/stock-ingredients/impact?commercantId=${commercantId}&ingredientId=${ingredient.id}`
      );
      setImpact(data);
      
      // Par défaut, tout est sélectionné
      setSelectedProduits(data.produitsSimples.map(p => p.id));
      setSelectedElements(data.elementsComposables.map(e => e.id));
    } catch (err) {
      console.error('Erreur chargement impact:', err);
      setError(err.message || 'Erreur lors du chargement de l\'impact');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm({
        produits: selectedProduits,
        elements: selectedElements
      });
    }
    onClose();
  };

  const toggleAllProduits = (checked) => {
    if (checked) {
      setSelectedProduits(impact.produitsSimples.map(p => p.id));
    } else {
      setSelectedProduits([]);
    }
  };

  const toggleAllElements = (checked) => {
    if (checked) {
      setSelectedElements(impact.elementsComposables.map(e => e.id));
    } else {
      setSelectedElements([]);
    }
  };

  const toggleProduit = (produitId) => {
    if (selectedProduits.includes(produitId)) {
      setSelectedProduits(selectedProduits.filter(id => id !== produitId));
    } else {
      setSelectedProduits([...selectedProduits, produitId]);
    }
  };

  const toggleElement = (elementId) => {
    if (selectedElements.includes(elementId)) {
      setSelectedElements(selectedElements.filter(id => id !== elementId));
    } else {
      setSelectedElements([...selectedElements, elementId]);
    }
  };

  const allProduitsSelected = impact && selectedProduits.length === impact.produitsSimples.length;
  const allElementsSelected = impact && selectedElements.length === impact.elementsComposables.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Mettre "${ingredient?.nom}" en rupture ?`}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-6">
        {/* Message d'avertissement */}
        <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              Les produits suivants seront désactivés. <strong>Décochez ceux que vous souhaitez conserver</strong> (par exemple, si vous avez encore de cet ingrédient pour certains produits).
            </p>
          </div>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-600">Chargement de l'impact...</p>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {impact && !loading && (
          <>
            {/* Produits simples */}
            {impact.produitsSimples.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Package className="h-5 w-5 text-gray-600" />
                    <h3 className="font-semibold text-gray-900">Produits simples</h3>
                    <span className="text-sm text-gray-500">
                      ({selectedProduits.length}/{impact.produitsSimples.length} sélectionnés)
                    </span>
                  </div>
                  <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                    <input
                      type="checkbox"
                      checked={allProduitsSelected}
                      onChange={(e) => toggleAllProduits(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Tout sélectionner</span>
                  </label>
                </div>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-64 overflow-y-auto">
                  {impact.produitsSimples.map(produit => (
                    <label
                      key={produit.id}
                      className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProduits.includes(produit.id)}
                        onChange={() => toggleProduit(produit.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{produit.nom}</p>
                        {produit.desactiveParIngredient && (
                          <p className="text-xs text-orange-600 mt-0.5">
                            Déjà désactivé par ingrédient
                          </p>
                        )}
                      </div>
                      {selectedProduits.includes(produit.id) ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Éléments composables */}
            {impact.elementsComposables.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Layers className="h-5 w-5 text-gray-600" />
                    <h3 className="font-semibold text-gray-900">Éléments composables</h3>
                    <span className="text-sm text-gray-500">
                      ({selectedElements.length}/{impact.elementsComposables.length} sélectionnés)
                    </span>
                  </div>
                  <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                    <input
                      type="checkbox"
                      checked={allElementsSelected}
                      onChange={(e) => toggleAllElements(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Tout sélectionner</span>
                  </label>
                </div>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-64 overflow-y-auto">
                  {impact.elementsComposables.map(element => (
                    <label
                      key={element.id}
                      className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedElements.includes(element.id)}
                        onChange={() => toggleElement(element.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">{element.nom}</p>
                          {!element.actif && (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-500">
                              Inactif
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          dans "{element.produitNom}"
                        </p>
                        {element.desactiveParIngredient && (
                          <p className="text-xs text-orange-600 mt-0.5">
                            Déjà désactivé par ingrédient
                          </p>
                        )}
                      </div>
                      {selectedElements.includes(element.id) ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Message si aucun produit impacté */}
            {impact.produitsSimples.length === 0 && impact.elementsComposables.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>Aucun produit n'utilise cet ingrédient.</p>
              </div>
            )}

            {/* Résumé */}
            {(selectedProduits.length > 0 || selectedElements.length > 0) && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>{selectedProduits.length + selectedElements.length} élément(s)</strong> seront désactivés :
                  {selectedProduits.length > 0 && ` ${selectedProduits.length} produit(s) simple(s)`}
                  {selectedProduits.length > 0 && selectedElements.length > 0 && ' et '}
                  {selectedElements.length > 0 && ` ${selectedElements.length} élément(s) composable(s)`}
                </p>
                {(impact.produitsSimples.length - selectedProduits.length > 0 || 
                  impact.elementsComposables.length - selectedElements.length > 0) && (
                  <p className="text-sm text-blue-700 mt-1">
                    {impact.produitsSimples.length - selectedProduits.length + 
                     impact.elementsComposables.length - selectedElements.length} élément(s) seront conservés.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || (selectedProduits.length === 0 && selectedElements.length === 0)}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirmer la rupture
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ImpactProduitsModal;

